import { Suspense, lazy } from 'react'
import { Navigate } from 'react-router-dom'
import { RouteObject } from 'react-router'
import SidebarLayout from './layouts/SidebarLayout'
import PrivateRoute from './layouts/PrivateRoute'
import OfflineRoute from './layouts/Offline'
import SuspenseLoader from './components/SuspenseLoader'
import ViewSlips from './content/applications/slips/ViewSlips'
import MyStaff from './content/applications/Staff'
import QuotationItem from './content/applications/CurrentAccounts/Quotation'
import OnboardingScreen from './content/applications/onboarding'
import HeaderLayout from './layouts/HeaderLayout'
import BaseLayout from './layouts/BaseLayout'
import WebsiteLayout from './layouts/WebsiteLayout'
import StartTesting from './content/applications/UITest'
import FeatureAnnouncementTest from './content/applications/FeatureAnnouncementTest'
import PendingBills from './content/applications/PendingBills'
import TnCView from './content/applications/TnC'
import BillingReport from './content/applications/Reports'
import BillsPaymentReport from './content/applications/BillsPaymentReport'
import SitesRentReport from './content/applications/SiteRentReport'
import SitesMonthlyRentReport from './content/applications/SiteMonthlyRentReport'
import AllSitesLedger from './content/applications/BillingSummary'
import Ledger from './content/applications/Ledger'
import LedgerSummary from './content/applications/LedgerSummary'
import SitesStockSummary from './content/applications/StockSummary'
import RecentBills from './content/applications/RecentBills'
import { ADMIN_PANEL_PATH } from './content/admin/constants/routes'
import { FeatureAnnouncementProvider } from './contexts/FeatureAnnouncementContext'

const Loader = (Component: React.ElementType) => (props: any) => {
  return (
    <Suspense fallback={null}>
      <Component {...props} />
    </Suspense>
  )
}

const CurrentAccounts = Loader(
  lazy(() => import('./content/applications/CurrentAccounts'))
)

const AllChallans = Loader(
  lazy(() => import('./content/applications/AllChallans'))
)
const RestoreBackup = Loader(
  lazy(() => import('./content/applications/RestoreBackup'))
)
const AllBills = Loader(lazy(() => import('./content/applications/AllBills')))
const SaleInvoices = Loader(
  lazy(() => import('./content/applications/SaleInvoices'))
)
const PaymentHistory = Loader(lazy(() => import('./content/applications/PaymentHistory')))
const GSTR3BReport = Loader(lazy(() => import('./content/applications/GSTR3B')))
const GSTR1Summary = Loader(
  lazy(() => import('./content/applications/GSTR1Summary'))
)

const Bill = Loader(lazy(() => import('./content/applications/Bill')))

const Customers = Loader(lazy(() => import('./content/applications/Customers')))
const ItemCategory = Loader(
  lazy(() => import('./content/applications/ItemCategory'))
)
const ItemHistoryPage = Loader(
  lazy(() => import('./content/applications/ItemCategory/ItemHistoryPage'))
)
const StockTransactions = Loader(
  lazy(() => import('./content/applications/StockTransactions'))
)
const Vendors = Loader(lazy(() => import('./content/applications/Vendors')))
const Items = Loader(lazy(() => import('./content/applications/Items')))
const PendingItems = Loader(
  lazy(() => import('./content/applications/PendingItems'))
)

const UserProfile = Loader(
  lazy(() => import('./content/applications/Users/profile'))
)

const UserLogin = Loader(
  lazy(() => import('./content/applications/Users/login'))
)

const ActivityPage = Loader(
  lazy(() => import('./content/applications/Activity'))
)

const TaskComponent = Loader(lazy(() => import('./content/applications/Task')))

const ContactUs = Loader(lazy(() => import('./content/applications/Contact')))

const NewQuotation = Loader(lazy(() => import('./content/applications/NewQuotation')))
const Quotations = Loader(lazy(() => import('./content/applications/Quotations')))
const Agreements = Loader(lazy(() => import('./content/applications/Agreements')))
const TableConfigurations = Loader(lazy(() => import('./content/applications/TableConfigurations')))
const DataImport = Loader(lazy(() => import('./content/applications/DataImport')))

// Website Pages
const PricingPage = Loader(lazy(() => import('./content/pages/Pricing')))
const AboutPage = Loader(lazy(() => import('./content/pages/About')))
const ContactPage = Loader(lazy(() => import('./content/pages/Contact')))
const ProductPage = Loader(lazy(() => import('./content/pages/Product')))
const HomePage = Loader(lazy(() => import('./content/pages/Home')))
const PrivacyPolicyPage = Loader(
  lazy(() => import('./content/pages/PrivacyPolicy'))
)
const ShippingPolicyPage = Loader(
  lazy(() => import('./content/pages/ShippingPolicy'))
)
const TermsConditionsPage = Loader(
  lazy(() => import('./content/pages/TermsConditions'))
)
const CancellationRefundPolicyPage = Loader(
  lazy(() => import('./content/pages/CancellationRefundPolicy'))
)
const AccountDataDeletionPage = Loader(
  lazy(() => import('./content/pages/AccountDataDeletion'))
)
const TutorialsPage = Loader(
  lazy(() => import('./content/pages/Tutorials'))
)
const TutorialDetailPage = Loader(
  lazy(() => import('./content/pages/Tutorials/TutorialDetail'))
)
const VideoTutorialsPage = Loader(
  lazy(() => import('./content/pages/VideoTutorials'))
)
const BlogPage = Loader(
  lazy(() => import('./content/pages/Blog'))
)
const BlogDetailPage = Loader(
  lazy(() => import('./content/pages/Blog/BlogDetail'))
)

// Admin Pages
const AdminLayout = Loader(
  lazy(() => import('./content/admin/layout/AdminLayout'))
)
const AdminDashboard = Loader(
  lazy(() => import('./content/admin/pages/Dashboard'))
)
const SoftwareInvoices = Loader(
  lazy(() => import('./content/admin/SoftwareInvoices'))
)
const SearchStores = Loader(
  lazy(() => import('./content/admin/SearchStores'))
)
const ReferenceStores = Loader(
  lazy(() => import('./content/admin/ReferenceStores'))
)
const RecentlySignedupStores = Loader(
  lazy(() => import('./content/admin/RecentlySignedupStores'))
)
const SubscriptionReport = Loader(
  lazy(() => import('./content/admin/SubscriptionReport'))
)
const ExpiringSubscriptions = Loader(
  lazy(() => import('./content/admin/ExpiringSubscriptions'))
)
const StorePaymentSummary = Loader(
  lazy(() => import('./content/admin/StorePaymentSummary'))
)

const UserSettings = Loader(
  lazy(() => import('./content/applications/Users/settings'))
)
const ChangePassword = Loader(
  lazy(() => import('./content/applications/Users/change-password'))
)

const SubscriptionPlan = Loader(
  lazy(() => import('./content/applications/Users/subscription-plan'))
)
const ChangeStoreComponent = Loader(
  lazy(() => import('./content/applications/Users/changeStore'))
)

const Status404 = Loader(lazy(() => import('./content/pages/Status/Status404')))
const Status500 = Loader(lazy(() => import('./content/pages/Status/Status500')))
const StatusComingSoon = Loader(
  lazy(() => import('./content/pages/Status/ComingSoon'))
)
const StatusMaintenance = Loader(
  lazy(() => import('./content/pages/Status/Maintenance'))
)

const TejseLayout = Loader(lazy(() => import('./content/tejse/layout/TejseLayout')))
const TejseHome = Loader(lazy(() => import('./content/tejse/pages/Home')))
const TejseAbout = Loader(lazy(() => import('./content/tejse/pages/About')))
const TejseProducts = Loader(lazy(() => import('./content/tejse/pages/Products')))
const TejseProductDetail = Loader(lazy(() => import('./content/tejse/pages/ProductDetail')))
const TejseBlog = Loader(lazy(() => import('./content/tejse/pages/Blog')))
const TejseBlogDetail = Loader(lazy(() => import('./content/tejse/pages/BlogDetail')))
const TejseContact = Loader(lazy(() => import('./content/tejse/pages/Contact')))

const routes = (isLoggedIn): RouteObject[] => [
  {
    path: '',
    element: <WebsiteLayout />,
    children: [
      {
        path: '',
        element: (
          <OfflineRoute>
            <HomePage />
          </OfflineRoute>
        ),
      },
      {
        path: 'pricing',
        element: (
          <OfflineRoute>
            <PricingPage />
          </OfflineRoute>
        ),
      },
      {
        path: 'products',
        element: (
          <OfflineRoute>
            <ProductPage />
          </OfflineRoute>
        ),
      },
      {
        path: 'about',
        element: (
          <OfflineRoute>
            <AboutPage />
          </OfflineRoute>
        ),
      },
      {
        path: 'contact',
        element: (
          <OfflineRoute>
            <ContactPage />
          </OfflineRoute>
        ),
      },
      {
        path: 'privacy-policy',
        element: (
          <OfflineRoute>
            <PrivacyPolicyPage />
          </OfflineRoute>
        ),
      },
      {
        path: 'shipping-policy',
        element: (
          <OfflineRoute>
            <ShippingPolicyPage />
          </OfflineRoute>
        ),
      },
      {
        path: 'refund-and-cancelation',
        element: (
          <OfflineRoute>
            <CancellationRefundPolicyPage />
          </OfflineRoute>
        ),
      },
      {
        path: 'terms-and-conditions',
        element: (
          <OfflineRoute>
            <TermsConditionsPage />
          </OfflineRoute>
        ),
      },
      {
        path: 'account-data-deletion',
        element: (
          <OfflineRoute>
            <AccountDataDeletionPage />
          </OfflineRoute>
        ),
      },
      {
        path: 'tutorials',
        element: (
          <OfflineRoute>
            <TutorialsPage />
          </OfflineRoute>
        ),
      },
      {
        path: 'tutorials/:slug',
        element: (
          <OfflineRoute>
            <TutorialDetailPage />
          </OfflineRoute>
        ),
      },
      {
        path: 'video-tutorials',
        element: (
          <OfflineRoute>
            <VideoTutorialsPage />
          </OfflineRoute>
        ),
      },
      {
        path: 'blog',
        element: (
          <OfflineRoute>
            <BlogPage />
          </OfflineRoute>
        ),
      },
      {
        path: 'blog/:slug',
        element: (
          <OfflineRoute>
            <BlogDetailPage />
          </OfflineRoute>
        ),
      },
    ],
  },
  {
    path: 'status',
    children: [
      {
        path: '',
        element: <Navigate to="404" replace />,
      },
      {
        path: '404',
        element: <Status404 />,
      },
      {
        path: '500',
        element: <Status500 />,
      },
      {
        path: 'maintenance',
        element: <StatusMaintenance />,
      },
      {
        path: 'coming-soon',
        element: <StatusComingSoon />,
      },
    ],
  },
  {
    path: 'tejse',
    element: <TejseLayout />,
    children: [
      {
        path: '',
        element: (
          <OfflineRoute>
            <TejseHome />
          </OfflineRoute>
        ),
      },
      {
        path: 'about',
        element: (
          <OfflineRoute>
            <TejseAbout />
          </OfflineRoute>
        ),
      },
      {
        path: 'products',
        element: (
          <OfflineRoute>
            <TejseProducts />
          </OfflineRoute>
        ),
      },
      {
        path: 'products/:slug',
        element: (
          <OfflineRoute>
            <TejseProductDetail />
          </OfflineRoute>
        ),
      },
      {
        path: 'blog',
        element: (
          <OfflineRoute>
            <TejseBlog />
          </OfflineRoute>
        ),
      },
      {
        path: 'blog/:slug',
        element: (
          <OfflineRoute>
            <TejseBlogDetail />
          </OfflineRoute>
        ),
      },
      {
        path: 'contact',
        element: (
          <OfflineRoute>
            <TejseContact />
          </OfflineRoute>
        ),
      },
    ],
  },
  // Admin Pages (obfuscated path)
  {
    path: ADMIN_PANEL_PATH,
    element: <AdminLayout />,
    children: [
      {
        path: '',
        element: <AdminDashboard />,
      },
      {
        path: 'software-invoices',
        element: <SoftwareInvoices />,
      },
      {
        path: 'search-stores',
        element: <SearchStores />,
      },
      {
        path: 'reference-stores',
        element: <ReferenceStores />,
      },
      {
        path: 'recently-signedup-stores',
        element: <RecentlySignedupStores />,
      },
      {
        path: 'subscription-report',
        element: <SubscriptionReport />,
      },
      {
        path: 'expiring-subscriptions',
        element: <ExpiringSubscriptions />,
      },
    ],
  },
  {
    path: '*',
    element: <Status404 />,
  },
  {
    path: 'onboarding',
    element: (
      <FeatureAnnouncementProvider>
        <BaseLayout />
      </FeatureAnnouncementProvider>
    ),
    children: [
      {
        path: '',
        element: <OnboardingScreen />,
      },
    ],
  },
  {
    path: 'login',
    element: !isLoggedIn ? <UserLogin /> : <Navigate to="/dashboard" replace />,
  },
  {
    path: '',
    element: <HeaderLayout />,
    children: [
      {
        path: 'select-store',
        element: (
          <PrivateRoute>
            <ChangeStoreComponent />
          </PrivateRoute>
        ),
      },
    ],
  },

  {
    path: 'create-bill',
    element: <SidebarLayout />,
    children: [
      {
        path: ':bill_id',
        element: <Bill />,
      },
    ],
  },
  {
    path: 'dashboard',
    element: (
      <PrivateRoute>
        <SidebarLayout />
      </PrivateRoute>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="current-account" replace />,
      },
      {
        path: 'current-account',
        element: (
          <PrivateRoute>
            <CurrentAccounts />
          </PrivateRoute>
        ),
      },
      {
        path: 'activities',
        element: (
          <PrivateRoute>
            <ActivityPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'test-announcements',
        element: (
          <PrivateRoute>
            <FeatureAnnouncementTest />
          </PrivateRoute>
        ),
      },
    ],
  },
  {
    path: 'management',
    element: (
      <PrivateRoute>
        <SidebarLayout />
      </PrivateRoute>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="customers" replace />,
      },
      {
        path: 'customers',
        element: (
          <PrivateRoute>
            <Customers />
          </PrivateRoute>
        ),
      },
      {
        path: 'new-quotation',
        element: (
          <PrivateRoute>
            <NewQuotation />
          </PrivateRoute>
        ),
      },
      {
        path: 'quotations',
        element: (
          <PrivateRoute>
            <Quotations />
          </PrivateRoute>
        ),
      },
      {
        path: 'agreements',
        element: (
          <PrivateRoute>
            <Agreements />
          </PrivateRoute>
        ),
      },
      {
        path: 'table-configurations',
        element: (
          <PrivateRoute>
            <TableConfigurations />
          </PrivateRoute>
        ),
      },
      {
        path: 'tnc',
        element: (
          <PrivateRoute>
            <TnCView />
          </PrivateRoute>
        ),
      },
      {
        path: 'import-data',
        element: (
          <PrivateRoute>
            <DataImport />
          </PrivateRoute>
        ),
      },
      {
        path: 'profile',
        children: [
          {
            path: '',
            element: <Navigate to="details" replace />,
          },
          {
            path: 'details',
            element: (
              <PrivateRoute>
                <UserProfile />
              </PrivateRoute>
            ),
          },
          {
            path: 'due_invoices',
            element: (
              <PrivateRoute>
                <PendingBills />
              </PrivateRoute>
            ),
          },
          {
            path: 'settings',
            element: (
              <PrivateRoute>
                <UserSettings />
              </PrivateRoute>
            ),
          },
          {
            path: 'change-password',
            element: (
              <PrivateRoute>
                <ChangePassword />
              </PrivateRoute>
            ),
          },
          {
            path: 'subscription-plans',
            element: (
              <PrivateRoute>
                <SubscriptionPlan />
              </PrivateRoute>
            ),
          },
          {
            path: 'change',
            element: (
              <PrivateRoute>
                <ChangeStoreComponent />
              </PrivateRoute>
            ),
          },
        ],
      },
      {
        path: 'tasks',
        element: (
          <PrivateRoute>
            <TaskComponent />
          </PrivateRoute>
        ),
      },
    ],
  },
  {
    path: 'stock',
    element: (
      <PrivateRoute>
        <SidebarLayout />
      </PrivateRoute>
    ),
    children: [
      {
        path: '',
        element: (
          <PrivateRoute>
            {' '}
            <Navigate to="item-category" replace />
          </PrivateRoute>
        ),
      },
      {
        path: 'item-category',
        element: (
          <PrivateRoute>
            {' '}
            <ItemCategory />
          </PrivateRoute>
        ),
      },
      {
        path: 'stock-transactions',
        element: (
          <PrivateRoute>
            <StockTransactions />
          </PrivateRoute>
        ),
      },
      {
        path: 'vendors',
        element: (
          <PrivateRoute>
            <Vendors />
          </PrivateRoute>
        ),
      },
      {
        path: 'items',
        children: [
          {
            path: '',
            element: (
              <PrivateRoute>
                {' '}
                <Items />
              </PrivateRoute>
            ),
          },
          {
            path: ':category_id',
            element: (
              <PrivateRoute>
                {' '}
                <Items />
              </PrivateRoute>
            ),
          },
        ],
      },
      {
        path: 'items/history/:item_id',
        element: (
          <PrivateRoute>
            <ItemHistoryPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'pending-items',
        children: [
          {
            path: ':item_id',
            element: (
              <PrivateRoute>
                {' '}
                <PendingItems />
              </PrivateRoute>
            ),
          },
        ],
      },
    ],
  },
  {
    path: 'testing',
    children: [
      {
        path: 'auto',
        children: [
          {
            path: ':token',
            element: <StartTesting />,
          },
        ],
      },
    ],
  },
  {
    path: 'slips',
    element: isLoggedIn ? <SidebarLayout /> : <Navigate to="/login" replace />,
    children: [
      {
        path: 'view-slips',
        children: [
          {
            path: ':bill_id/:customer_id',
            element: <ViewSlips />,
          },
        ],
      },
      {
        path: 'by-date',
        children: [
          {
            path: ':screen_type',
            element: <ViewSlips />,
          },
        ],
      },
    ],
  },
  {
    path: 'reports',
    element: isLoggedIn ? <SidebarLayout /> : <Navigate to="/login" replace />,
    children: [
      {
        path: 'all-bills',
        children: [
          {
            path: '',
            element: <AllBills />,
          },
        ],
      },
      {
        path: 'sale-invoices',
        children: [
          {
            path: '',
            element: <SaleInvoices />,
          },
        ],
      },
      {
        path: 'payment-history',
        children: [
          {
            path: '',
            element: <PaymentHistory />,
          },
        ],
      },
    ],
  },
  {
    path: 'bills',
    element: isLoggedIn ? <SidebarLayout /> : <Navigate to="/login" replace />,
    children: [
      {
        path: 'recent-bills',
        children: [
          {
            path: '',
            element: <RecentBills />,
          },
        ],
      },
    ],
  },
  {
    path: 'reports',
    element: isLoggedIn ? <SidebarLayout /> : <Navigate to="/login" replace />,
    children: [
      {
        path: 'gstr1',
        children: [
          {
            path: '',
            element: <GSTR1Summary />,
          },
        ],
      },
    ],
  },
  {
    path: 'charts',
    element: isLoggedIn ? <SidebarLayout /> : <Navigate to="/login" replace />,
    children: [
      {
        path: 'bills-payment-chart',
        children: [
          {
            path: '',
            element: <BillsPaymentReport />,
          },
        ],
      },
    ],
  },
  {
    path: 'charts',
    element: isLoggedIn ? <SidebarLayout /> : <Navigate to="/login" replace />,
    children: [
      {
        path: 'billing-chart',
        children: [
          {
            path: '',
            element: <BillingReport />,
          },
        ],
      },
    ],
  },
  {
    path: 'charts',
    element: isLoggedIn ? <SidebarLayout /> : <Navigate to="/login" replace />,
    children: [
      {
        path: 'site-wise-rent',
        children: [
          {
            path: '',
            element: <SitesRentReport />,
          },
        ],
      },
    ],
  },
  {
    path: 'charts',
    element: isLoggedIn ? <SidebarLayout /> : <Navigate to="/login" replace />,
    children: [
      {
        path: 'site-monthly-rent',
        children: [
          {
            path: '',
            element: <SitesMonthlyRentReport />,
          },
        ],
      },
    ],
  },
  {
    path: 'charts',
    element: isLoggedIn ? <SidebarLayout /> : <Navigate to="/login" replace />,
    children: [
      {
        path: 'billing-summary',
        children: [
          {
            path: '',
            element: <AllSitesLedger />,
          },
        ],
      },
    ],
  },
  {
    path: 'charts',
    element: isLoggedIn ? <SidebarLayout /> : <Navigate to="/login" replace />,
    children: [
      {
        path: 'ledger-summary',
        children: [
          {
            path: '',
            element: <LedgerSummary />,
          },
        ],
      },
    ],
  },
  {
    path: 'charts',
    element: isLoggedIn ? <SidebarLayout /> : <Navigate to="/login" replace />,
    children: [
      {
        path: 'sites-stock-summary',
        children: [
          {
            path: '',
            element: <SitesStockSummary />,
          },
        ],
      },
    ],
  },
  {
    path: 'rrwerrwrrwrwr',
    element: <AdminLayout />,
    children: [
      {
        path: 'asdhhdsdhahfhshdfhhsfshfshfshfhsdf',
        children: [
          {
            path: '',
            element: <StorePaymentSummary />,
          },
        ],
      },
    ],
  },
  {
    path: 'reports',
    element: isLoggedIn ? <SidebarLayout /> : <Navigate to="/login" replace />,
    children: [
      {
        path: 'gstr-3b',
        children: [
          {
            path: '',
            element: <GSTR3BReport />,
          },
        ],
      },
    ],
  },
  {
    path: 'more',
    element: isLoggedIn ? <SidebarLayout /> : <Navigate to="/login" replace />,
    children: [
      {
        path: 'all-challans',
        children: [
          {
            path: ':challan_type',
            element: <AllChallans />,
          },
        ],
      },
      {
        path: 'restore-backup',
        children: [
          {
            path: '',
            element: <RestoreBackup />,
          },
        ],
      },
    ],
  },
  {
    path: 'quotation',
    element: isLoggedIn ? <SidebarLayout /> : <Navigate to="/login" replace />,
    children: [
      {
        path: 'view-quotation',
        children: [
          {
            path: ':bill_id',
            element: <QuotationItem />,
          },
        ],
      },
    ],
  },
  {
    path: 'staff',
    element: isLoggedIn ? <SidebarLayout /> : <Navigate to="/login" replace />,
    children: [
      {
        path: 'my-staff',
        children: [
          {
            path: '',
            element: <MyStaff />,
          },
        ],
      },
    ],
  },
  ,
  {
    path: 'support',
    element: isLoggedIn ? <SidebarLayout /> : <Navigate to="/" replace />,
    children: [
      {
        path: 'contact',
        children: [
          {
            path: '',
            element: <ContactUs />,
          },
        ],
      },
    ],
  },
]

export default routes
